import React, { useState, useEffect } from "react";
import { addFeatureToTenant, getAllFeatures } from "../../../../Services/BrokersApi";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Button,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  MenuItem,
  IconButton,
  Avatar,
  InputAdornment,
  Divider,
  Card,
  Chip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Add as AddIcon,
  Stars as StarsIcon,
  Category as CategoryIcon,
  AttachMoney as AttachMoneyIcon,
  CalendarToday as CalendarTodayIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { themeColors } from "../../../../theme/colors";

const AddFeatureDialog = ({ open, onClose, tenantId, activeFeatures = [], onFeatureAdded, onSuccess, availableFeatures = null, selectedPlanFeatures = [] }) => {
  const [form, setForm] = useState({
    feature_id: "",
    value: "",
    yearly_cost: "",
    monthly_cost: "",
    start_date: "",
    end_date: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // new: features list & loading state
  const [features, setFeatures] = useState([]);
  const [loadingFeatures, setLoadingFeatures] = useState(false);

  // قائمة المميزات المفعّلة الحالية (تستخدم selectedPlanFeatures إن وُجدت كأولوية)
  const activeList = Array.isArray(selectedPlanFeatures) && selectedPlanFeatures.length > 0
    ? selectedPlanFeatures
    : (Array.isArray(activeFeatures) ? activeFeatures : []);
  // مجموعة معرفات المميزات المفعّلة لتسهيل الفلترة
  const activeIds = new Set(
    activeList.map((f) => f?.id ?? f?.pivot?.feature_id).filter(Boolean)
  );

  // إذا تغيّر الـ tenant أو الحوّال أفرغ النموذج
  useEffect(() => {
    if (!open) {
      setForm({
        feature_id: "",
        value: "",
        yearly_cost: "",
        monthly_cost: "",
        start_date: "",
        end_date: "",
      });
      setError(null);
      setSubmitting(false);
    }
  }, [open, tenantId]);

  // new: جلب الفيتشرات عند فتح الـ Dialog
  useEffect(() => {
    let mounted = true;
    const fetchFeatures = async () => {
      if (!open) return;
      // إذا تم تمرير availableFeatures من الأب، استخدمها بدلاً من جلبها
      if (Array.isArray(availableFeatures) && availableFeatures.length > 0) {
        setLoadingFeatures(false);
        if (mounted) setFeatures(availableFeatures);
        return;
      }
      setLoadingFeatures(true);
      try {
        const token = localStorage.getItem("authToken");
        const res = await getAllFeatures(token);
        // API قد تعيد { features: [...] } أو مصفوفة مباشرة
        const list = res && res.features ? res.features : Array.isArray(res) ? res : [];
        if (mounted) setFeatures(list);
      } catch (err) {
        // لا نعطل الـ Dialog عند فشل جلب الفيتشرات، نعرض رسالة في console فقط
        console.error("Failed to load features", err);
      } finally {
        if (mounted) setLoadingFeatures(false);
      }
    };
    fetchFeatures();
    return () => {
      mounted = false;
    };
  }, [open]);
  const handleChange = (field) => (e) => setForm((s) => ({ ...s, [field]: e.target.value }));

  const handleSubmit = async () => {
    if (!tenantId) return;
    if (!form.feature_id) {
      setError("الرجاء إدخال feature_id");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const token = localStorage.getItem("authToken");
      // أضف tenant_id داخل جسم الطلب لضمان وصوله للـ API
      const payload = { tenant_id: tenantId, ...form };
      // نمرّر tenantId كمعامل أول كما كان (إن كانت دالة الـ API تتوقعه)، ونمرّر payload كجسم الطلب
      await addFeatureToTenant(tenantId, payload, token);
      console.log("Feature added successfully");
      // نُعلم المكوّن الأب بأي اسم دالة تم تمريره (حافظ على التوافق)
      if (typeof onFeatureAdded === "function") await onFeatureAdded();
      // استدعاء onSuccess إن وُجدت (AllVersionTap يستخدم onSuccess عادة)
      if (typeof onSuccess === "function") await onSuccess();
      onClose?.();
    } catch (err) {
      setError((err && (err.message || err.msg || JSON.stringify(err))) || "حدث خطأ");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !submitting && onClose?.()}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          pb: 2,
          background: `linear-gradient(135deg, ${themeColors.success} 0%, #45A049 100%)`,
          color: "white",
          position: "relative",
        }}
      >
        <Avatar
          sx={{
            width: 48,
            height: 48,
            bgcolor: "rgba(255,255,255,0.25)",
            backdropFilter: "blur(10px)",
          }}
        >
          <AddIcon />
        </Avatar>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            إضافة ميزة جديدة
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            إضافة ميزة إضافية للنسخة
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={() => !submitting && onClose?.()}
          sx={{
            position: "absolute",
            left: 8,
            top: 8,
            color: "white",
            "&:hover": {
              bgcolor: "rgba(255,255,255,0.15)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {/* أعلى الـ Dialog: عرض الميزات المفعّلة حالياً فقط */}
        {/* Active Features Section */}
        {(() => {
          // إذا تم تمرير selectedPlanFeatures من الأب استخدمها، وإلا استخدم activeFeatures كـ fallback
          const activeList = Array.isArray(selectedPlanFeatures) && selectedPlanFeatures.length > 0 ? selectedPlanFeatures : (Array.isArray(activeFeatures) ? activeFeatures : []);
          if (activeList.length === 0) return null;
          return (
            <Card
              elevation={0}
              sx={{
                p: 2.5,
                mb: 3,
                borderRadius: 2,
                border: `1px solid ${themeColors.border.light}`,
                bgcolor: `${themeColors.success}08`,
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <CheckCircleIcon sx={{ color: themeColors.success }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  الميزات المفعّلة حالياً
                </Typography>
                <Chip
                  label={activeList.length}
                  size="small"
                  sx={{
                    bgcolor: themeColors.success,
                    color: "white",
                    fontWeight: 600,
                  }}
                />
              </Box>
              <Divider sx={{ mb: 2 }} />
              <List dense sx={{ p: 0 }}>
                {activeList.map((f, idx) => {
                  const pivotFeatureId = f?.pivot?.feature_id;
                  const featureNameFromList = pivotFeatureId
                    ? features.find((x) => x.id === pivotFeatureId)?.name
                    : null;
                  const primaryName =
                    f.name ??
                    f.plane_name ??
                    featureNameFromList ??
                    String(f.id ?? "-");
                  const secondary = `قيمة: ${f.pivot?.value ?? "-"} • شهري: ${
                    f.pivot?.monthly_cost ?? "-"
                  } • سنوي: ${f.pivot?.yearly_cost ?? "-"}`;
                  return (
                    <React.Fragment key={f.id ?? pivotFeatureId ?? Math.random()}>
                      <ListItem
                        sx={{
                          px: 0,
                          py: 1.5,
                          "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {primaryName}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" sx={{ color: themeColors.text.secondary }}>
                              {secondary}
                            </Typography>
                          }
                        />
                      </ListItem>
                      {idx < activeList.length - 1 && <Divider />}
                    </React.Fragment>
                  );
                })}
              </List>
            </Card>
          );
        })()}

        {!((Array.isArray(selectedPlanFeatures) && selectedPlanFeatures.length > 0) || (Array.isArray(activeFeatures) && activeFeatures.length > 0)) && (
          <Alert
            severity="info"
            sx={{
              mb: 3,
              borderRadius: 2,
              bgcolor: `${themeColors.info}08`,
              border: `1px solid ${themeColors.info}30`,
            }}
          >
            لا توجد ميزات مفعّلة حالياً
          </Alert>
        )}

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(220, 53, 69, 0.15)",
            }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Form Section */}
        <Card
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 2,
            border: `1px solid ${themeColors.border.light}`,
            bgcolor: themeColors.background.default,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, mb: 2.5, color: themeColors.text.primary }}
          >
            📝 بيانات الميزة الجديدة
          </Typography>

          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <TextField
                select
                label="اختر الميزة"
                fullWidth
                value={form.feature_id}
                onChange={handleChange("feature_id")}
                required
                disabled={loadingFeatures}
                helperText={
                  loadingFeatures
                    ? "جاري تحميل الميزات..."
                    : features.length === 0
                    ? "⚠️ لا توجد ميزات متاحة"
                    : `✓ ${features.length} ميزة متاحة`
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CategoryIcon sx={{ color: themeColors.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: themeColors.success,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: themeColors.success,
                    },
                  },
                }}
              >
                <MenuItem value="">-- اختر ميزة --</MenuItem>
                {features
                  .filter((feat) => !activeIds.has(feat.id)) // استبعاد المميزات المفعّلة
                  .map((feat) => (
                    <MenuItem key={feat.id} value={feat.id}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <StarsIcon sx={{ fontSize: 16, color: themeColors.success }} />
                        {feat.name}
                      </Box>
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="القيمة"
                placeholder="مثال: 1"
                fullWidth
                value={form.value}
                onChange={handleChange("value")}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <StarsIcon sx={{ color: themeColors.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: themeColors.success,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: themeColors.success,
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="التكلفة الشهرية"
                placeholder="0.00"
                fullWidth
                value={form.monthly_cost}
                onChange={handleChange("monthly_cost")}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon sx={{ color: themeColors.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: themeColors.success,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: themeColors.success,
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="التكلفة السنوية"
                placeholder="0.00"
                fullWidth
                value={form.yearly_cost}
                onChange={handleChange("yearly_cost")}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon sx={{ color: themeColors.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: themeColors.success,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: themeColors.success,
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="تاريخ البداية"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.start_date}
                onChange={handleChange("start_date")}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarTodayIcon sx={{ color: themeColors.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: themeColors.success,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: themeColors.success,
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="تاريخ النهاية"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.end_date}
                onChange={handleChange("end_date")}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarTodayIcon sx={{ color: themeColors.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: themeColors.success,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: themeColors.success,
                    },
                  },
                }}
              />
            </Grid>
          </Grid>
        </Card>

        <Box
          sx={{
            mt: 3,
            p: 2,
            bgcolor: `${themeColors.success}08`,
            borderRadius: 2,
            border: `1px solid ${themeColors.success}30`,
          }}
        >
          <Typography variant="caption" sx={{ color: themeColors.text.secondary }}>
            💡 <strong>ملاحظة:</strong> سيتم إضافة الميزة مباشرة إلى النسخة بعد الحفظ
          </Typography>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2.5, gap: 1 }}>
        <Button
          onClick={() => !submitting && onClose?.()}
          disabled={submitting}
          variant="outlined"
          sx={{
            borderRadius: 2,
            px: 3,
            borderColor: themeColors.border.main,
            color: themeColors.text.secondary,
            "&:hover": {
              borderColor: themeColors.text.primary,
              bgcolor: "transparent",
            },
          }}
        >
          إلغاء
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || loadingFeatures || !form.feature_id}
          sx={{
            borderRadius: 2,
            px: 4,
            bgcolor: themeColors.success,
            boxShadow: `0 4px 12px ${themeColors.success}40`,
            "&:hover": {
              bgcolor: "#45A049",
              boxShadow: `0 6px 16px ${themeColors.success}60`,
            },
            "&:disabled": {
              bgcolor: themeColors.text.light,
            },
          }}
        >
          {submitting ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} color="inherit" />
              <span>جاري الإضافة...</span>
            </Box>
          ) : (
            <>
              <AddIcon sx={{ mr: 1, fontSize: 20 }} />
              إضافة الميزة
            </>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddFeatureDialog;
